import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  FileText, Save, ArrowLeft, Loader2, AlertTriangle, CheckCircle2, 
  XCircle, ChevronDown, Calculator, Package, AlertCircle, Info
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const IRCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const poNumberParam = searchParams.get('po');

  const [loading, setLoading] = useState(false);
  const [loadingPOs, setLoadingPOs] = useState(true);
  const [loadingPOData, setLoadingPOData] = useState(false);
  const [simulatingMatch, setSimulatingMatch] = useState(false);

  // Master data
  const [poList, setPoList] = useState([]);
  const [grList, setGrList] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);

  // Selected PO data
  const [selectedPO, setSelectedPO] = useState(poNumberParam || '');
  const [poData, setPoData] = useState(null);

  // 3-Way Match Result
  const [matchResult, setMatchResult] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    invoice_type: 'STANDARD',
    vendor_id: '',
    vendor_invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    posting_date: new Date().toISOString().split('T')[0],
    baseline_date: '',
    company_code: '',
    payment_terms: '',
    payment_method: '',
    reference: '',
    header_text: '',
    unplanned_delivery_cost: 0,
    freight_amount: 0,
    calculate_tax: true,
    gr_based_iv: true,
    created_by: 'ADMIN'
  });

  // Invoice Items
  const [items, setItems] = useState([]);
  const [activeItemIndex, setActiveItemIndex] = useState(0);

  // Load initial data
  useEffect(() => {
    loadMasterData();
  }, []);

  // Load PO data when selected
  useEffect(() => {
    if (selectedPO) {
      loadPOForInvoicing(selectedPO);
    }
  }, [selectedPO]);

  // Master data for dropdowns
  const [glAccounts, setGlAccounts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);

  const loadMasterData = async () => {
    try {
      setLoadingPOs(true);
      const [posRes, vendorsRes, ccRes, glRes, costCenterRes, paymentTermsRes, taxCodesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/invoice-receipts/pos-for-invoicing`),
        axios.get(`${BACKEND_URL}/api/vendor-master/vendors`),
        axios.get(`${BACKEND_URL}/api/company/company-codes`),
        axios.get(`${BACKEND_URL}/api/gl-accounts`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/controlling/cost-centers`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/payment-terms`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/tax-codes`).catch(() => ({ data: [] }))
      ]);
      setPoList(posRes.data);
      setVendors(vendorsRes.data);
      setCompanyCodes(ccRes.data);
      setGlAccounts(glRes.data || []);
      setCostCenters(costCenterRes.data || []);
      setPaymentTerms(paymentTermsRes.data || []);
      setTaxCodes(taxCodesRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Failed to load master data');
    } finally {
      setLoadingPOs(false);
    }
  };

  const loadPOForInvoicing = async (poNumber) => {
    try {
      setLoadingPOData(true);
      
      // Get PO details and GRs
      const [poRes, grsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/purchase-orders/${poNumber}`),
        axios.get(`${BACKEND_URL}/api/invoice-receipts/grs-for-po/${poNumber}`)
      ]);
      
      const po = poRes.data;
      setPoData(po);
      setGrList(grsRes.data);
      
      // Pre-fill form with PO data
      setFormData(prev => ({
        ...prev,
        vendor_id: po.vendor_id,
        company_code: po.company_code,
        payment_terms: po.payment_terms || '',
        header_text: `Invoice for PO ${po.po_number}`
      }));
      
      // Create items from PO items that have open quantity
      const invoiceItems = po.items
        .filter(item => {
          const received = parseFloat(item.quantity_received || 0);
          const invoiced = parseFloat(item.quantity_invoiced || 0);
          return received > invoiced; // Only items with open quantity
        })
        .map((item, idx) => {
          const received = parseFloat(item.quantity_received || 0);
          const invoiced = parseFloat(item.quantity_invoiced || 0);
          const openQty = received - invoiced;
          const poPrice = parseFloat(item.net_price || 0) / parseInt(item.price_unit || 1);
          
          return {
            item_number: (idx + 1) * 10,
            selected: true,
            po_number: po.po_number,
            po_item: item.item_number,
            gr_document: '',
            gr_year: '',
            material_id: item.material_id || '',
            material_description: item.short_text || '',
            plant: item.plant || '',
            quantity: openQty,
            unit_of_measure: item.unit_of_measure || 'EA',
            unit_price: poPrice,
            price_unit: 1,
            currency: item.currency || po.currency || 'USD',
            tax_code: item.tax_code || '',
            tax_rate: parseFloat(item.tax_percentage || 0),
            tax_amount: 0,
            gl_account: item.gl_account || '',
            cost_center: item.cost_center || '',
            // Reference data for display
            po_quantity: parseFloat(item.po_quantity || item.quantity || 0),
            po_price: poPrice,
            quantity_received: received,
            quantity_invoiced: invoiced,
            open_quantity: openQty
          };
        });
      
      setItems(invoiceItems);
      setActiveItemIndex(0);
      setMatchResult(null);
      
      if (invoiceItems.length === 0) {
        toast.error('No items available for invoicing on this PO');
      } else {
        toast.success(`Loaded ${invoiceItems.length} item(s) from PO ${poNumber}`);
      }
    } catch (error) {
      console.error('Error loading PO:', error);
      toast.error('Failed to load PO data');
    } finally {
      setLoadingPOData(false);
    }
  };

  const handlePOSelect = (poNumber) => {
    setSelectedPO(poNumber);
    if (!poNumber) {
      setPoData(null);
      setItems([]);
      setMatchResult(null);
      setFormData(prev => ({
        ...prev,
        vendor_id: '',
        company_code: '',
        payment_terms: '',
        header_text: ''
      }));
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    // Auto-calculate tax amount if tax_rate changes
    if (field === 'tax_rate' || field === 'quantity' || field === 'unit_price') {
      const qty = parseFloat(newItems[index].quantity || 0);
      const price = parseFloat(newItems[index].unit_price || 0);
      const taxRate = parseFloat(newItems[index].tax_rate || 0);
      newItems[index].tax_amount = (qty * price * taxRate / 100).toFixed(2);
    }
    
    setItems(newItems);
    setMatchResult(null); // Clear match result when data changes
  };

  const handleItemSelect = (index, selected) => {
    const newItems = [...items];
    newItems[index].selected = selected;
    setItems(newItems);
    setMatchResult(null);
  };

  const simulateMatch = async () => {
    const selectedItems = items.filter(item => item.selected);
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return;
    }
    
    if (!formData.vendor_invoice_number) {
      toast.error('Please enter vendor invoice number');
      return;
    }
    
    try {
      setSimulatingMatch(true);
      
      const payload = {
        ...formData,
        items: selectedItems.map(item => ({
          item_number: item.item_number,
          po_number: item.po_number,
          po_item: item.po_item,
          gr_document: item.gr_document || null,
          gr_year: item.gr_year || null,
          material_id: item.material_id,
          material_description: item.material_description,
          plant: item.plant,
          quantity: parseFloat(item.quantity),
          unit_of_measure: item.unit_of_measure,
          unit_price: parseFloat(item.unit_price),
          price_unit: parseInt(item.price_unit) || 1,
          currency: item.currency,
          tax_code: item.tax_code,
          tax_rate: parseFloat(item.tax_rate) || 0,
          tax_amount: parseFloat(item.tax_amount) || 0,
          gl_account: item.gl_account,
          cost_center: item.cost_center
        }))
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/invoice-receipts/simulate-match`, payload);
      setMatchResult(response.data);
      
      if (response.data.match_status === 'MATCHED') {
        toast.success('3-Way Match: All items matched within tolerance!');
      } else if (response.data.match_status === 'VARIANCE') {
        toast.error('3-Way Match: Variance detected - review before posting');
      } else if (response.data.match_status === 'BLOCKED') {
        toast.error('3-Way Match: Invoice will be blocked due to variance');
      }
    } catch (error) {
      console.error('Error simulating match:', error);
      toast.error(error.response?.data?.detail || 'Failed to simulate 3-way match');
    } finally {
      setSimulatingMatch(false);
    }
  };

  const validateForm = () => {
    if (!formData.vendor_id) {
      toast.error('Vendor is required');
      return false;
    }
    if (!formData.vendor_invoice_number) {
      toast.error('Vendor invoice number is required');
      return false;
    }
    if (!formData.company_code) {
      toast.error('Company code is required');
      return false;
    }
    
    const selectedItems = items.filter(item => item.selected);
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return false;
    }
    
    for (const item of selectedItems) {
      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        toast.error(`Item ${item.po_item}: Valid quantity is required`);
        return false;
      }
      if (item.unit_price === '' || parseFloat(item.unit_price) < 0) {
        toast.error(`Item ${item.po_item}: Valid unit price is required`);
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      const selectedItems = items.filter(item => item.selected);
      
      const payload = {
        ...formData,
        unplanned_delivery_cost: parseFloat(formData.unplanned_delivery_cost) || 0,
        freight_amount: parseFloat(formData.freight_amount) || 0,
        items: selectedItems.map(item => ({
          item_number: item.item_number,
          po_number: item.po_number,
          po_item: item.po_item,
          gr_document: item.gr_document || null,
          gr_year: item.gr_year || null,
          material_id: item.material_id,
          material_description: item.material_description,
          plant: item.plant,
          quantity: parseFloat(item.quantity),
          unit_of_measure: item.unit_of_measure,
          unit_price: parseFloat(item.unit_price),
          price_unit: parseInt(item.price_unit) || 1,
          currency: item.currency,
          tax_code: item.tax_code,
          tax_rate: parseFloat(item.tax_rate) || 0,
          tax_amount: parseFloat(item.tax_amount) || 0,
          gl_account: item.gl_account,
          cost_center: item.cost_center
        }))
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/invoice-receipts`, payload);
      toast.success(`Invoice ${response.data.invoice_document} created successfully!`);
      navigate(`/p2p/ir-view/${response.data.invoice_document}/${response.data.fiscal_year}`);
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const selectedItems = items.filter(item => item.selected);
  const totalNet = selectedItems.reduce((sum, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.unit_price) || 0;
    return sum + (qty * price);
  }, 0);
  const totalTax = selectedItems.reduce((sum, item) => sum + (parseFloat(item.tax_amount) || 0), 0);
  const totalGross = totalNet + totalTax + parseFloat(formData.unplanned_delivery_cost || 0) + parseFloat(formData.freight_amount || 0);

  const currentItem = items[activeItemIndex];

  const getMatchStatusColor = (status) => {
    const colors = {
      'MATCHED': 'bg-green-100 text-green-800 border-green-200',
      'VARIANCE': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'BLOCKED': 'bg-red-100 text-red-800 border-red-200',
      'FAILED': 'bg-red-100 text-red-800 border-red-200',
      'PENDING': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || colors['PENDING'];
  };

  const getMatchStatusIcon = (status) => {
    const icons = {
      'MATCHED': <CheckCircle2 className="w-5 h-5 text-green-600" />,
      'VARIANCE': <AlertTriangle className="w-5 h-5 text-yellow-600" />,
      'BLOCKED': <XCircle className="w-5 h-5 text-red-600" />,
      'FAILED': <XCircle className="w-5 h-5 text-red-600" />
    };
    return icons[status] || <Info className="w-5 h-5 text-gray-600" />;
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/p2p/ir-list')}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-7 h-7 text-orange-600" />
                Enter Invoice Receipt (MIRO)
              </h1>
              <p className="text-sm text-gray-500">Create invoice with 3-way matching (PO-GR-IR)</p>
            </div>
          </div>
        </div>

        {/* PO Selection Section */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Purchase Order Reference</h2>
          <div className="flex gap-4 items-end">
            <div className="flex-1 max-w-lg">
              <Label>Select Purchase Order *</Label>
              <div className="relative">
                <select
                  value={selectedPO}
                  onChange={(e) => handlePOSelect(e.target.value)}
                  disabled={loadingPOs || loadingPOData}
                  className="w-full mt-1 px-4 py-2.5 border border-gray-300 rounded-md text-sm appearance-none bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 pr-10"
                >
                  <option value="">-- Select a PO with Open Quantity --</option>
                  {poList.map((po) => (
                    <option key={po.po_number} value={po.po_number}>
                      {po.po_number} | {po.vendor_name || po.vendor_id} | Open: {po.open_for_invoice?.toFixed(2)} | {po.currency} {po.total_value?.toLocaleString()}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 mt-0.5 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
              {loadingPOs && <p className="text-xs text-gray-500 mt-1">Loading Purchase Orders...</p>}
            </div>
            {loadingPOData && (
              <div className="flex items-center text-orange-600">
                <Loader2 className="animate-spin w-5 h-5 mr-2" />
                Loading PO Details...
              </div>
            )}
          </div>
          
          {poList.length === 0 && !loadingPOs && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2" />
                No Purchase Orders with open quantity for invoicing. Post Goods Receipt first.
              </p>
            </div>
          )}
        </div>

        {/* Show form only when PO is selected */}
        {poData && items.length > 0 && (
          <form onSubmit={handleSubmit}>
            {/* PO Summary */}
            <div className="bg-gradient-to-r from-orange-50 to-orange-50 rounded-lg border border-orange-200 p-4 mb-6">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <p className="text-xs text-orange-600">Vendor</p>
                  <p className="font-semibold text-orange-900">{poData.vendor_id} - {poData.vendor_name}</p>
                </div>
                <div>
                  <p className="text-xs text-orange-600">Company Code</p>
                  <p className="font-semibold text-orange-900">{poData.company_code}</p>
                </div>
                <div>
                  <p className="text-xs text-orange-600">Currency</p>
                  <p className="font-semibold text-orange-900">{poData.currency}</p>
                </div>
                <div>
                  <p className="text-xs text-orange-600">PO Status</p>
                  <p className="font-semibold text-orange-900">{poData.status}</p>
                </div>
                <div>
                  <p className="text-xs text-orange-600">Items for Invoice</p>
                  <p className="font-semibold text-green-700">{items.length} item(s)</p>
                </div>
              </div>
            </div>

            {/* Invoice Header */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Header</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Invoice Type *</Label>
                  <select
                    value={formData.invoice_type}
                    onChange={(e) => setFormData({...formData, invoice_type: e.target.value})}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="STANDARD">Standard Invoice (RE)</option>
                    <option value="CREDIT_MEMO">Credit Memo (KG)</option>
                    <option value="DEBIT_MEMO">Debit Memo (DG)</option>
                  </select>
                </div>
                <div>
                  <Label>Vendor Invoice No. *</Label>
                  <Input
                    value={formData.vendor_invoice_number}
                    onChange={(e) => setFormData({...formData, vendor_invoice_number: e.target.value})}
                    placeholder="VINV-2025-001"
                    required
                  />
                </div>
                <div>
                  <Label>Invoice Date *</Label>
                  <Input
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => setFormData({...formData, invoice_date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Posting Date *</Label>
                  <Input
                    type="date"
                    value={formData.posting_date}
                    onChange={(e) => setFormData({...formData, posting_date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Baseline Date</Label>
                  <Input
                    type="date"
                    value={formData.baseline_date}
                    onChange={(e) => setFormData({...formData, baseline_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Payment Terms</Label>
                  <select
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({...formData, payment_terms: e.target.value})}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                  >
                    <option value="">-- Select Payment Terms --</option>
                    {paymentTerms.length > 0 ? (
                      paymentTerms.map(pt => (
                        <option key={pt.payment_terms_key || pt.id} value={pt.payment_terms_key || pt.id}>
                          {pt.payment_terms_key || pt.id} - {pt.description || pt.name || 'Payment Terms'}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="Z001">Z001 - Net 30 Days</option>
                        <option value="Z002">Z002 - Net 45 Days</option>
                        <option value="Z003">Z003 - Net 60 Days</option>
                        <option value="N30">N30 - Net 30 Days</option>
                        <option value="N45">N45 - Net 45 Days</option>
                        <option value="N60">N60 - Net 60 Days</option>
                        <option value="0001">0001 - Payable Immediately</option>
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <Label>Reference</Label>
                  <Input
                    value={formData.reference}
                    onChange={(e) => setFormData({...formData, reference: e.target.value})}
                    placeholder="Reference number"
                    maxLength={16}
                  />
                </div>
                <div>
                  <Label>Unplanned Delivery Cost</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.unplanned_delivery_cost}
                    onChange={(e) => setFormData({...formData, unplanned_delivery_cost: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Header Text</Label>
                  <Input
                    value={formData.header_text}
                    onChange={(e) => setFormData({...formData, header_text: e.target.value})}
                    placeholder="Invoice notes..."
                    maxLength={200}
                  />
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={formData.gr_based_iv}
                      onCheckedChange={(checked) => setFormData({...formData, gr_based_iv: checked})}
                    />
                    <Label className="text-sm">GR-Based IV</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={formData.calculate_tax}
                      onCheckedChange={(checked) => setFormData({...formData, calculate_tax: checked})}
                    />
                    <Label className="text-sm">Calculate Tax</Label>
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Items */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Invoice Line Items</h2>
                <div className="text-sm text-gray-500">
                  {selectedItems.length} of {items.length} items selected
                </div>
              </div>

              {/* Item Selector Dropdown */}
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="flex-1 max-w-md">
                  <Label className="text-sm text-gray-600">Select Item to Edit</Label>
                  <div className="relative">
                    <select
                      value={activeItemIndex}
                      onChange={(e) => setActiveItemIndex(parseInt(e.target.value))}
                      className="w-full mt-1 px-4 py-2.5 border border-gray-300 rounded-md text-sm appearance-none bg-white pr-10"
                    >
                      {items.map((item, index) => (
                        <option key={index} value={index}>
                          Item {item.po_item} | {item.material_id} | Open: {item.open_quantity?.toFixed(2)} {item.unit_of_measure} {item.selected ? '✓' : '○'}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 mt-0.5 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setItems(items.map(i => ({...i, selected: true})))}>
                    Select All
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setItems(items.map(i => ({...i, selected: false})))}>
                    Deselect All
                  </Button>
                </div>
              </div>

              {/* Current Item Details */}
              {currentItem && (
                <div className="border-t pt-4">
                  {/* Item Selection Checkbox */}
                  <div className="flex items-center space-x-3 mb-4 p-3 bg-gray-50 rounded-md">
                    <Checkbox
                      checked={currentItem.selected}
                      onCheckedChange={(checked) => handleItemSelect(activeItemIndex, checked)}
                    />
                    <Label className="text-sm font-medium">
                      Invoice this item (PO Item {currentItem.po_item})
                    </Label>
                  </div>

                  {/* Material Info */}
                  <div className="bg-orange-50 p-3 rounded-md mb-4">
                    <p className="font-medium text-orange-900">
                      {currentItem.material_id} - {currentItem.material_description}
                    </p>
                    <p className="text-sm text-orange-700">
                      Plant: {currentItem.plant} | UOM: {currentItem.unit_of_measure}
                    </p>
                  </div>

                  {/* Quantities Comparison Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4 p-4 bg-gray-50 rounded-md">
                    <div>
                      <Label className="text-xs text-gray-500">PO Quantity</Label>
                      <p className="font-semibold text-gray-900">{currentItem.po_quantity?.toFixed(2)}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">GR Received</Label>
                      <p className="font-semibold text-green-700">{currentItem.quantity_received?.toFixed(2)}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Already Invoiced</Label>
                      <p className="font-semibold text-orange-700">{currentItem.quantity_invoiced?.toFixed(2)}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Open for Invoice</Label>
                      <p className="font-semibold text-yellow-700">{currentItem.open_quantity?.toFixed(2)}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">PO Price</Label>
                      <p className="font-semibold text-gray-900">{currentItem.currency} {currentItem.po_price?.toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Invoice Input Fields */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label>Invoice Quantity *</Label>
                      <Input
                        type="number"
                        step="0.001"
                        value={currentItem.quantity}
                        onChange={(e) => handleItemChange(activeItemIndex, 'quantity', e.target.value)}
                        className={parseFloat(currentItem.quantity) > currentItem.open_quantity ? 'border-red-500' : ''}
                      />
                      {parseFloat(currentItem.quantity) > currentItem.open_quantity && (
                        <p className="text-xs text-red-500 mt-1">Exceeds open quantity!</p>
                      )}
                    </div>
                    <div>
                      <Label>Unit Price *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={currentItem.unit_price}
                        onChange={(e) => handleItemChange(activeItemIndex, 'unit_price', e.target.value)}
                        className={Math.abs(parseFloat(currentItem.unit_price) - currentItem.po_price) / currentItem.po_price > 0.05 ? 'border-yellow-500' : ''}
                      />
                      {currentItem.po_price > 0 && Math.abs(parseFloat(currentItem.unit_price) - currentItem.po_price) / currentItem.po_price > 0.05 && (
                        <p className="text-xs text-yellow-600 mt-1">Price differs from PO!</p>
                      )}
                    </div>
                    <div>
                      <Label>Tax Code</Label>
                      <select
                        value={currentItem.tax_code || ''}
                        onChange={(e) => {
                          const selectedTax = taxCodes.find(t => t.tax_code === e.target.value);
                          handleItemChange(activeItemIndex, 'tax_code', e.target.value);
                          if (selectedTax) {
                            handleItemChange(activeItemIndex, 'tax_rate', selectedTax.tax_percentage || 0);
                          }
                        }}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                      >
                        <option value="">-- Select Tax Code --</option>
                        {taxCodes.length > 0 ? (
                          taxCodes.map(tc => (
                            <option key={tc.tax_code || tc.id} value={tc.tax_code || tc.id}>
                              {tc.tax_code || tc.id} - {tc.description || tc.name || 'Tax'} ({tc.tax_percentage || 0}%)
                            </option>
                          ))
                        ) : (
                          <>
                            <option value="V0">V0 - Tax Exempt (0%)</option>
                            <option value="V1">V1 - Standard Tax (10%)</option>
                            <option value="V2">V2 - Reduced Tax (5%)</option>
                            <option value="I0">I0 - Input Tax Exempt (0%)</option>
                            <option value="I1">I1 - Input Tax Standard (10%)</option>
                          </>
                        )}
                      </select>
                    </div>
                    <div>
                      <Label>Tax Rate (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={currentItem.tax_rate}
                        onChange={(e) => handleItemChange(activeItemIndex, 'tax_rate', e.target.value)}
                        className="bg-gray-50"
                        readOnly={!!currentItem.tax_code}
                      />
                    </div>
                    <div>
                      <Label>GR Document</Label>
                      <select
                        value={currentItem.gr_document}
                        onChange={(e) => handleItemChange(activeItemIndex, 'gr_document', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="">-- Select GR (Optional) --</option>
                        {grList.map(gr => (
                          <option key={gr.material_document} value={gr.material_document}>
                            {gr.material_document} | {gr.posting_date} | Qty: {gr.total_quantity}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>G/L Account</Label>
                      <select
                        value={currentItem.gl_account}
                        onChange={(e) => handleItemChange(activeItemIndex, 'gl_account', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                      >
                        <option value="">-- Select G/L Account --</option>
                        {currentItem.gl_account && !glAccounts.find(g => g.account_number === currentItem.gl_account) && (
                          <option value={currentItem.gl_account}>{currentItem.gl_account} (From PO)</option>
                        )}
                        {glAccounts.map(gl => (
                          <option key={gl.account_number || gl.id} value={gl.account_number || gl.id}>
                            {gl.account_number || gl.id} - {gl.account_name || gl.description || 'G/L Account'}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Cost Center</Label>
                      <select
                        value={currentItem.cost_center}
                        onChange={(e) => handleItemChange(activeItemIndex, 'cost_center', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                      >
                        <option value="">-- Select Cost Center --</option>
                        {currentItem.cost_center && !costCenters.find(c => c.cost_center_id === currentItem.cost_center) && (
                          <option value={currentItem.cost_center}>{currentItem.cost_center} (From PO)</option>
                        )}
                        {costCenters.map(cc => (
                          <option key={cc.cost_center_id || cc.id} value={cc.cost_center_id || cc.id}>
                            {cc.cost_center_id || cc.id} - {cc.name || cc.description || 'Cost Center'}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Item Amount */}
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-green-700">Item Net Amount</span>
                      <span className="font-bold text-green-900">
                        {currentItem.currency} {(parseFloat(currentItem.quantity || 0) * parseFloat(currentItem.unit_price || 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 3-Way Match Section */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-orange-600" />
                  3-Way Match (PO - GR - Invoice)
                </h2>
                <Button
                  type="button"
                  variant="outline"
                  onClick={simulateMatch}
                  disabled={simulatingMatch || selectedItems.length === 0}
                >
                  {simulatingMatch ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Simulating...</>
                  ) : (
                    <><Calculator className="w-4 h-4 mr-2" />Simulate Match</>
                  )}
                </Button>
              </div>

              {matchResult ? (
                <div className={`p-4 rounded-lg border ${getMatchStatusColor(matchResult.match_status)}`}>
                  <div className="flex items-center gap-3 mb-3">
                    {getMatchStatusIcon(matchResult.match_status)}
                    <span className="font-semibold text-lg">{matchResult.match_status}</span>
                  </div>
                  <p className="text-sm mb-3">{matchResult.summary}</p>
                  
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="text-center p-2 bg-white/50 rounded">
                      <p className="text-xs text-gray-600">Quantity Variance</p>
                      <p className={`font-bold ${matchResult.total_quantity_variance !== 0 ? 'text-yellow-700' : 'text-green-700'}`}>
                        {matchResult.total_quantity_variance?.toFixed(2)}
                      </p>
                    </div>
                    <div className="text-center p-2 bg-white/50 rounded">
                      <p className="text-xs text-gray-600">Price Variance</p>
                      <p className={`font-bold ${Math.abs(matchResult.total_price_variance) > 0 ? 'text-yellow-700' : 'text-green-700'}`}>
                        {matchResult.total_price_variance?.toFixed(2)}
                      </p>
                    </div>
                    <div className="text-center p-2 bg-white/50 rounded">
                      <p className="text-xs text-gray-600">Amount Variance</p>
                      <p className={`font-bold ${Math.abs(matchResult.total_amount_variance) > 100 ? 'text-red-700' : 'text-green-700'}`}>
                        {matchResult.total_amount_variance?.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {matchResult.block_reasons?.length > 0 && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                      <p className="text-sm font-medium text-red-800 mb-2">Block Reasons:</p>
                      <ul className="text-sm text-red-700 list-disc list-inside">
                        {matchResult.block_reasons.map((reason, idx) => (
                          <li key={idx}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calculator className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Click "Simulate Match" to preview 3-way matching results</p>
                </div>
              )}
            </div>

            {/* Invoice Totals */}
            <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Totals</h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Items Selected</p>
                  <p className="text-xl font-bold text-gray-900">{selectedItems.length}</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg text-center">
                  <p className="text-xs text-orange-600">Net Amount</p>
                  <p className="text-xl font-bold text-orange-900">{poData?.currency} {totalNet.toFixed(2)}</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg text-center">
                  <p className="text-xs text-orange-600">Tax Amount</p>
                  <p className="text-xl font-bold text-orange-900">{poData?.currency} {totalTax.toFixed(2)}</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg text-center">
                  <p className="text-xs text-orange-600">Add. Costs</p>
                  <p className="text-xl font-bold text-orange-900">
                    {poData?.currency} {(parseFloat(formData.unplanned_delivery_cost || 0) + parseFloat(formData.freight_amount || 0)).toFixed(2)}
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-xs text-green-600">Gross Amount</p>
                  <p className="text-xl font-bold text-green-900">{poData?.currency} {totalGross.toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate('/p2p/ir-list')}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading || selectedItems.length === 0} className="bg-orange-600 hover:bg-orange-700">
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" />Create Invoice</>
                )}
              </Button>
            </div>
          </form>
        )}

        {/* Empty state when no PO selected */}
        {!poData && !loadingPOData && (
          <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a Purchase Order</h3>
            <p className="text-gray-600">Choose a PO from the dropdown above to start creating an invoice</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default IRCreate;
